// The questions a task's step waits on, pinned at the top of its page (S141): what was asked
// and when, and a way to the answer form. The questions are end-to-end encrypted, so they are
// read and answered in the conversation (one form per question on the page); without a
// linked browser the pin says how to get one.
import { Hand } from "@phosphor-icons/react";
import { Button } from "rizzui";
import { Link } from "react-router";

import { useDevice } from "../../device/context";
import { useT } from "../../i18n";
import { formatAgo } from "../../i18n/format";
import { cardClass } from "../../ui/Card";
import { cn } from "../../ui/cn";
import type { Task } from "../model";

/** Where the conversation card lists the pending questions (`PendingQuestions`). */
export const QUESTIONS_ANCHOR = "task-questions";

export function QuestionsPin({ task }: { task: Pick<Task, "waiting_on"> }) {
  const t = useT().tasks.waiting;
  const device = useDevice();
  const waiting = task.waiting_on;
  if (waiting?.kind !== "questions") return null;
  const toForm = () => document.getElementById(QUESTIONS_ANCHOR)?.scrollIntoView({ behavior: "smooth", block: "start" });
  return (
    <section aria-label={t.pinLabel} data-testid="questions-pin" className={cn(cardClass, "mb-4 flex flex-wrap items-center gap-3 border-orange/50 p-4")}>
      <Hand weight="duotone" className="h-6 w-6 shrink-0 text-orange" aria-hidden />
      <div className="min-w-0 flex-1">
        <h3 className="m-0 text-[14px] font-semibold text-gray-900">{t.pinTitle}</h3>
        <p className="m-0 text-[13px] text-gray-600">{t.pinBody(waiting.count, formatAgo(waiting.since))}</p>
        {device.kind === "unlinked" ? (
          <p className="m-0 mt-1 text-[12.5px] text-gray-500">
            {t.linkFirst} <Link to="/devices">{t.linkLink}</Link>
          </p>
        ) : null}
      </div>
      {device.kind === "linked" ? (
        <Button size="sm" onClick={toForm}>
          {t.answerBelow}
        </Button>
      ) : null}
    </section>
  );
}
